import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, Server, Mail } from 'lucide-react';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import SEO from '../common/SEO';
import '../../styles/Home.css';
import '../../styles/Auth.css';

export default function PrivacyPolicyPage() {
  return (
    <div className="senritsu-container">
      <SEO
        title="Privacy Policy | SenritsuX - Secure Independent Music Platform"
        description="Learn how SenritsuX protects your personal data, release metadata, audio files, and payment details with bank-grade encryption and privacy controls."
        canonical="/privacy"
        keywords={['SenritsuX privacy policy', 'music artist data privacy', 'creator data security']}
      />
      <Navbar />

      <main className="page-main-content">
        <section className="about-hero-section" style={{ paddingTop: '2.5rem', paddingBottom: '1.5rem' }}>
          <div className="about-hero-wrapper" style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <span className="page-badge">Data Protection</span>
            <h1 className="about-hero-title" style={{ fontSize: '2.4rem', marginTop: '0.5rem' }}>
              SenritsuX <span className="title-highlight">Privacy Policy</span>
            </h1>
            <p className="about-hero-subtitle" style={{ margin: '0 auto 1.5rem', maxWidth: '600px' }}>
              Effective Date: January 1, 2025 • Your trust and creator privacy are fundamental to everything we build.
            </p>
          </div>
        </section>

        <div style={{ maxWidth: '850px', margin: '0 auto 4rem', padding: '0 1.5rem' }}>
          <div
            className="auth-card"
            style={{
              padding: '2.5rem',
              borderRadius: '16px',
              lineHeight: 1.8,
              color: 'rgba(255,255,255,0.85)',
              fontSize: '0.98rem',
            }}
          >
            {/* 1. Introduction */}
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <Shield size={22} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                  1. Overview &amp; Commitment
                </h2>
              </div>
              <p>
                SenritsuX (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) operates the music ecosystem available at <strong style={{ color: '#fff' }}>https://senritsux.in</strong>. This Privacy Policy informs creators, visitors, and partners about how we collect, use, store, and safeguard your personal information and artistic assets.
              </p>
              <p>
                We respect your creative independence: <strong>we do not sell, rent, or monetize your raw audio files, stems, unpublished drafts, or personal credentials to third-party advertisers or AI model training networks without your explicit consent.</strong>
              </p>
            </div>

            {/* 2. Information We Collect */}
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <Database size={22} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                  2. Information We Collect
                </h2>
              </div>
              <p>Depending on how you interact with SenritsuX, we collect:</p>
              <ul style={{ paddingLeft: '1.2rem' }}>
                <li><strong>Account Data:</strong> Email address, legal name, stage name, profile avatar, and account authentication credentials managed via secure tokens.</li>
                <li><strong>Music &amp; Release Metadata:</strong> Song titles, album names, genre classifications, ISRC/UPC codes, composer/lyricist credits, audio master files, and cover artwork submitted for global distribution.</li>
                <li><strong>Financial &amp; Royalty Data:</strong> UPI ID, bank account details (IFSC, account number), or payout identifiers necessary to remit streaming royalties and beat marketplace earnings.</li>
                <li><strong>Technical &amp; Usage Telemetry:</strong> Anonymized browser information, IP address, device types, and page interaction metrics to diagnose technical bottlenecks and optimize platform speed.</li>
              </ul>
            </div>

            {/* 3. How We Use Information */}
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <Lock size={22} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                  3. How We Use Your Data
                </h2>
              </div>
              <ul style={{ paddingLeft: '1.2rem' }}>
                <li>To ingest and deliver your music to licensed streaming platforms (Spotify, Apple Music, etc.).</li>
                <li>To calculate, allocate, and transfer automated monthly royalty payouts.</li>
                <li>To showcase your public artist portfolio on Artist Web if enabled in your settings.</li>
                <li>To facilitate verified collaboration requests between registered musicians and producers.</li>
                <li>To prevent copyright infringement, duplicate uploads, and unauthorized stream gaming.</li>
              </ul>
            </div>

            {/* 4. Data Security & Storage */}
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <Server size={22} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                  4. Data Security &amp; Storage
                </h2>
              </div>
              <p>
                All account data and database records are safeguarded by Supabase enterprise-grade infrastructure with AES-256 encryption at rest and TLS 1.3 encryption in transit. Private creator files (drafts, unreleased master audio, identity documents) are protected by Row-Level Security (RLS) policies ensuring only authorized account holders and verified admins can access them.
              </p>
            </div>

            {/* 5. Your Rights */}
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <Eye size={22} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                  5. Your Rights &amp; Visibility Controls
                </h2>
              </div>
              <p>
                You have the full right to:
              </p>
              <ul style={{ paddingLeft: '1.2rem' }}>
                <li>Toggle your Artist Web public profile visibility on or off at any moment in Settings.</li>
                <li>Request access to an export of your personal metadata and discography release records.</li>
                <li>Request account deletion and takedown of distributed releases from DSPs.</li>
              </ul>
            </div>

            {/* 6. Contact */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <Mail size={22} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                  6. Contact Our Privacy Officer
                </h2>
              </div>
              <p>
                For privacy inquiries, data deletion requests, or questions regarding our security architecture, contact us:
              </p>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '1rem 1.2rem', borderRadius: '10px' }}>
                <p style={{ margin: '0 0 0.4rem' }}><strong>Email:</strong> <a href="mailto:support@senritsux.in" style={{ color: '#d9fb06' }}>support@senritsux.in</a></p>
                <p style={{ margin: 0 }}><strong>Address:</strong> SenritsuX Operations, India</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
